import React, { useEffect } from 'react';

const SingletonPattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Singleton Pattern - Creational Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Singleton Pattern - ensure a class has only one instance. Learn different implementation approaches, thread safety, and real-world examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Singleton Pattern - ensure a class has only one instance. Learn different implementation approaches, thread safety, and real-world examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'singleton pattern, design patterns, creational patterns, thread safety, software design, object oriented programming');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'singleton pattern, design patterns, creational patterns, thread safety, software design, object oriented programming';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Singleton Pattern</h1>
        <p style={styles.subtitle}>
          Creational Design Pattern - Ensure a class has only one instance
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The Singleton pattern ensures that a class has only one instance and provides a global point 
            of access to that instance. It's useful when exactly one object is needed to coordinate actions 
            across the system.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            Sometimes you need to ensure that a class has only one instance. For example:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Database connection pools</li>
            <li style={styles.listItem}>Configuration settings</li>
            <li style={styles.listItem}>Logging services</li>
            <li style={styles.listItem}>Cache managers</li>
            <li style={styles.listItem}>Thread pools</li>
          </ul>
          <p style={styles.paragraph}>
            Creating multiple instances of such classes can lead to resource waste, inconsistent state, 
            or conflicting operations.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Solution</h2>
          <p style={styles.paragraph}>
            The Singleton pattern solves this by:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Making the constructor private to prevent direct instantiation</li>
            <li style={styles.listItem}>Providing a static method that returns the single instance</li>
            <li style={styles.listItem}>Storing the instance in a static variable</li>
            <li style={styles.listItem}>Ensuring thread safety in multi-threaded environments</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Implementation</h2>
          
          <h3 style={styles.heading}>1. Eager Initialization</h3>
          <div style={styles.codeBlock}>
{`// Simple but creates instance even if not used
public class EagerSingleton {
    // Instance created at class loading time
    private static final EagerSingleton INSTANCE = new EagerSingleton();
    
    // Private constructor prevents instantiation
    private EagerSingleton() {
        // Prevent reflection-based instantiation
        if (INSTANCE != null) {
            throw new IllegalStateException("Singleton instance already exists");
        }
    }
    
    public static EagerSingleton getInstance() {
        return INSTANCE;
    }
    
    public void doSomething() {
        System.out.println("Doing something with eager singleton");
    }
}`}
          </div>

          <h3 style={styles.heading}>2. Thread-Safe Lazy Initialization</h3>
          <div style={styles.codeBlock}>
{`// Thread-safe but performance overhead due to synchronization
public class ThreadSafeSingleton {
    private static ThreadSafeSingleton instance;
    
    private ThreadSafeSingleton() {}
    
    public static synchronized ThreadSafeSingleton getInstance() {
        if (instance == null) {
            instance = new ThreadSafeSingleton();
        }
        return instance;
    }
    
    public void doSomething() {
        System.out.println("Doing something with thread-safe singleton");
    }
}`}
          </div>

          <h3 style={styles.heading}>3. Double-Checked Locking</h3>
          <div style={styles.codeBlock}>
{`// Optimized thread-safe version with double-checked locking
public class DoubleCheckedSingleton {
    // volatile ensures visibility across threads
    private static volatile DoubleCheckedSingleton instance;
    
    private DoubleCheckedSingleton() {}
    
    public static DoubleCheckedSingleton getInstance() {
        if (instance == null) { // First check (no locking)
            synchronized (DoubleCheckedSingleton.class) {
                if (instance == null) { // Second check (with locking)
                    instance = new DoubleCheckedSingleton();
                }
            }
        }
        return instance;
    }
    
    public void doSomething() {
        System.out.println("Doing something with double-checked singleton");
    }
}`}
          </div>

          <h3 style={styles.heading}>4. Enum Singleton (Recommended)</h3>
          <div style={styles.codeBlock}>
{`// Most robust - handles serialization and reflection automatically
public enum EnumSingleton {
    INSTANCE;
    
    public void doSomething() {
        System.out.println("Doing something with enum singleton");
    }
    
    // Can have other methods and fields
    private String value;
    
    public void setValue(String value) {
        this.value = value;
    }
    
    public String getValue() {
        return value;
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Advantages</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Controlled Access:</strong> Ensures only one instance exists</li>
            <li style={styles.listItem}><strong>Global Access Point:</strong> Provides easy access from anywhere in the application</li>
            <li style={styles.listItem}><strong>Lazy Initialization:</strong> Instance created only when needed (in some implementations)</li>
            <li style={styles.listItem}><strong>Memory Efficiency:</strong> Saves memory by preventing multiple instances</li>
            <li style={styles.listItem}><strong>Consistent State:</strong> Single instance maintains consistent state</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Disadvantages</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Global State:</strong> Can make testing difficult due to global state</li>
            <li style={styles.listItem}><strong>Tight Coupling:</strong> Classes become tightly coupled to the singleton</li>
            <li style={styles.listItem}><strong>Difficult to Mock:</strong> Hard to replace with mock objects for testing</li>
            <li style={styles.listItem}><strong>Thread Safety:</strong> Requires careful consideration in multi-threaded environments</li>
            <li style={styles.listItem}><strong>Hidden Dependencies:</strong> Makes dependencies less obvious</li>
            <li style={styles.listItem}><strong>Violates Single Responsibility:</strong> Manages its own lifecycle and business logic</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When exactly one instance of a class is needed</li>
            <li style={styles.listItem}>For managing shared resources (database connections, file systems)</li>
            <li style={styles.listItem}>For configuration settings that should be consistent across the application</li>
            <li style={styles.listItem}>For logging services that need centralized control</li>
            <li style={styles.listItem}>For caching mechanisms that should be shared</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Singleton pattern is useful when you need exactly one instance of a class, but it should 
            be used judiciously. While it provides controlled access to a single instance, it can make 
            code harder to test and maintain. Consider alternatives like dependency injection for better 
            testability and flexibility.
          </p>
          <p style={styles.paragraph}>
            When implementing Singleton, prefer the enum approach for its simplicity and built-in 
            serialization support, or use the Bill Pugh solution for lazy initialization with good performance.
          </p>
        </section>
      </main>
    </article>
  );
};

export default SingletonPattern;
